﻿/**
 * FragmentMountManager
 * Owns iframe DOM creation, registration, and unmounting.
 */
import type {
  Fragment,
  FragmentMountConfig,
  FragmentName,
  FragmentFormInputOptions,
  FragmentFormInputOptionKey,
} from '@anddone/ajs-util';
import { FRAGMENT_CONFIG } from '@anddone/ajs-util';
import type { Events } from './../Events';
import type { Orchestrator } from './../Orchestrator';
import type { StateManagement } from './../StateManagement';
import type { FragmentRegistry } from './../fragment-registry/FragmentRegistry';
import { ORCHESTRATOR_FRAGMENT_ELEMENT_ID } from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';
import { generateGuid, generateFragmentLifecycleContext } from '@anddone/ajs-util';

const logger = createLogger('SDK', 'FragmentMountManager');

function buildFragmentMountUrl(
  fragmentBaseUrl: string,
  fragmentName: FragmentName,
  options?: FragmentFormInputOptions,
): string {
  const basePath = `${fragmentBaseUrl.replace(/\/$/, '')}/${fragmentName}`;

  if (!options) {
    return basePath;
  }

  const searchParams = new URLSearchParams();

  for (const key of Object.keys(options) as FragmentFormInputOptionKey[]) {
    const value = options[key];

    if (value !== undefined) {
      searchParams.set(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
}

export class FragmentMountManager {
  /**
   * constructor
   *
   * @param dataState - Shared config and SDK state
   * @param orchestrator - Reference to orchestrator module
   * @param events - Reference to events module
   * @param fragmentRegistry - Reference to fragmentRegistry module
   */
  constructor(
    private readonly dataState: StateManagement,
    private readonly orchestrator: Orchestrator,
    private readonly events: Events,
    private readonly fragmentRegistry: FragmentRegistry,
  ) {}

  /**
   * Mount all configured fragments.
   *
   * @returns void
   */
  public mountAllFragments(): void {
    const fragmentsToLoad = Array.from(this.dataState.config.fragmentsToLoad);
    logger.info('mount', `Mounting ${fragmentsToLoad.length} configured fragment(s)`);
    fragmentsToLoad.forEach(config => this.mount(config));
    // Todo: Add validation that configured fragments are a valid combination.
    // E.g., cannot have cvv without card-number, and you can't have multiple of the same fragment type.
  }

  /**
   * Mount a fragment into a specified element
   *
   * @param config - Fragment mount configuration
   * @returns Fragment | null
   */
  public mount(config: FragmentMountConfig): Fragment | null {
    if (this.orchestrator.getSdkInitialized() === true) {
      logger.warn('mount', 'Cannot mount fragment — app already initialized', {
        fragmentName: config.fragmentName,
      });
      return null;
    }

    const { elementId, fragmentName, options } = config;

    if (!fragmentName) {
      logger.warn('mount', 'No fragmentName submitted. Fragment cannot mount.', {
        fragmentName: config.fragmentName,
      });
      return null;
    }

    const fragmentConfig = FRAGMENT_CONFIG.get(fragmentName);

    const iFrameHeight = fragmentConfig?.defaultFragmentHeight ?? 120;

    const targetElement = document.getElementById(elementId);

    if (!targetElement) {
      logger.error('mount', `Target element not found: "#${elementId}"`, {
        fragmentName,
        elementId,
      });
      return null;
    }

    const url = buildFragmentMountUrl(this.dataState.config.fragmentURL, fragmentName, options);
    logger.info('mount', `Mounting "${fragmentName}" → #${elementId}`, {
      url,
      height: iFrameHeight,
      options,
    });

    targetElement.innerHTML = `
      <div class="mounted mount-container">
        <iframe
          id="${fragmentName}Frame"
          src="${url}"
          height="${iFrameHeight}px"
          width="100%"
          scrolling="no"
          style="border: none; overflow: hidden; outline: none; margin: 0; padding: 0px; display: block;"
        ></iframe>
      </div>
    `;

    const fragment: Fragment = {
      id: generateGuid(),
      container: targetElement.querySelector('.mount-container') as HTMLElement,
      iframe: document.getElementById(`${fragmentName}Frame`) as HTMLIFrameElement,
      status: 'mounting',
      fragmentName: fragmentName,
      height: iFrameHeight,
      ...(options ? { options } : {}),
    };

    if (fragmentName === 'orchestrator') {
      this.fragmentRegistry.setOrchestrator(fragment);
      logger.debug('mount', 'Orchestrator fragment registered');
      return fragment;
    }

    const fragmentLifecycleContext = generateFragmentLifecycleContext(fragment);
    this.fragmentRegistry.set(fragmentName, fragment);
    this.events.emit('fragment-mounting', fragmentLifecycleContext);
    this.orchestrator.sendStatusUpdateCommandToOrchestratorFragment();

    logger.debug('mount', `"${fragmentName}" registered and status update dispatched`, {
      id: fragment.id,
    });

    return fragment;
  }

  /**
   * Mount the orchestrator fragment. This is the central communication hub. It is auto-mounted on init.
   *
   * @returns void
   */
  public mountOrchestratorFragment(): void {
    this.createOrchestratorFragmentDomElement(); // todo: Add option for consuming app to send this in via config

    const orchestratorFragmentConfig: FragmentMountConfig = {
      elementId: ORCHESTRATOR_FRAGMENT_ELEMENT_ID,
      fragmentName: 'orchestrator',
    };

    this.mount(orchestratorFragmentConfig);
  }

  /**
   * Adds the dom placeholder for the orchestrator fragment
   *
   * @returns HTMLDivElement
   */
  private createOrchestratorFragmentDomElement(): HTMLDivElement {
    const div = document.createElement('div');
    document.body.appendChild(div);
    div.id = ORCHESTRATOR_FRAGMENT_ELEMENT_ID;
    return div;
  }

  /**
   * Unmount a fragment
   *
   * @param fragmentName - Name of the fragment to unmount
   * @returns boolean - True if unmounted successfully, false otherwise
   */
  public unmount(fragmentName: FragmentName): boolean {
    const fragment = this.fragmentRegistry.get(fragmentName);

    if (!fragment || !fragment.container.parentElement) {
      logger.warn('mount', `Unmount failed — "${fragmentName}" not found or already removed`);
      return false;
    }

    fragment.container.parentElement.innerHTML = '';
    this.orchestrator.clearPendingCommandsForFragment(fragment);
    this.fragmentRegistry.delete(fragmentName);
    logger.info('mount', `"${fragmentName}" unmounted`);
    return true;
  }

  /**
   * Unmount all fragments
   *
   * @returns void
   */
  public unmountAll(): void {
    const names = this.fragmentRegistry.names();
    logger.info('mount', 'Unmounting all fragments', names);
    names.forEach(name => this.unmount(name));
  }
}
